import React from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { useChildWeeklyRatingVolumeByStarData } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import StackedTopEntityChart from 'src/components/BeaconV2Components/TopEntityChartV2/StackedTopEntityChart';
import { useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { getStartAndEndOfWeek } from 'src/utils/dateUtils';
import moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import { XAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/StackedTopEntityChart';
import { useStacklineTheme } from '@stackline/ui';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import LegendWithMultipleElement from 'src/components/BeaconV2Components/Legend/LegendWithMultipleElement';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { saveAs } from 'file-saver';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

export interface StackedChartDataSet {
  name: string;
  data: { y: number; color: string }[];
  color: string;
}

const ChildWeeklyRatingVolumeByStarRatingChart = () => {
  const theme = useStacklineTheme();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  const { data: childWeeklyRatingVolumeByStar, isLoading: childWeeklyRatingVolumeByStarLoading } =
    useChildWeeklyRatingVolumeByStarData();

  const categoryData: string[] = [];

  const stackedChartDataset: StackedChartDataSet[] = [
    { name: '1 Star', data: [], color: theme.colors.accentMagenta },
    { name: '2 Stars', data: [], color: theme.colors.accentTangerine },
    { name: '3 Stars', data: [], color: theme.colors.accentGold },
    { name: '4 Stars', data: [], color: theme.colors.success },
    { name: '5 Stars', data: [], color: theme.colors.accentPine }
  ];

  childWeeklyRatingVolumeByStar.forEach((datum) => {
    const {
      weekId,
      childWeeklyOneStarRatingCount_sum_value = 0,
      childWeeklyTwoStarsRatingCount_sum_value = 0,
      childWeeklyThreeStarsRatingCount_sum_value = 0,
      childWeeklyFourStarsRatingCount_sum_value = 0,
      childWeeklyFiveStarsRatingCount_sum_value = 0
    } = datum;

    if (weekId !== undefined) {
      categoryData.push(String(weekId));

      const dataItems = [
        childWeeklyOneStarRatingCount_sum_value,
        childWeeklyTwoStarsRatingCount_sum_value,
        childWeeklyThreeStarsRatingCount_sum_value,
        childWeeklyFourStarsRatingCount_sum_value,
        childWeeklyFiveStarsRatingCount_sum_value
      ];

      // if it is the latest week, we will push it as separate series, so it will always stay on the right side
      dataItems.forEach((value, dataItemIndex) => {
        stackedChartDataset[dataItemIndex].data.push({
          y: Math.round(value),
          color: stackedChartDataset[dataItemIndex].color
        });
      });
    }
  });

  if (childWeeklyRatingVolumeByStarLoading) {
    return <SplineChartLoading width={900} />;
  }

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const fileTitle = 'csv_data';
        const headers = ['Week ID', '1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'];

        try {
          const oneStarData = stackedChartDataset.find((data) => data.name === '1 Star')?.data;
          const twoStarsData = stackedChartDataset.find((data) => data.name === '2 Stars')?.data;
          const threeStarsData = stackedChartDataset.find((data) => data.name === '3 Stars')?.data;
          const fourStarsData = stackedChartDataset.find((data) => data.name === '4 Stars')?.data;
          const fiveStarsData = stackedChartDataset.find((data) => data.name === '5 Stars')?.data;

          const csvData = oneStarData.map((_, index) => {
            const weekId = categoryData[index];
            const oneStarDatum = oneStarData[index];
            const twoStarsDatum = twoStarsData[index];
            const threeStarsDatum = threeStarsData[index];
            const fourStarsDatum = fourStarsData[index];
            const fiveStarsDatum = fiveStarsData[index];

            return [weekId, oneStarDatum.y, twoStarsDatum.y, threeStarsDatum.y, fourStarsDatum.y, fiveStarsDatum.y];
          });
          const csvDataString = [headers, ...csvData].map((row) => row.join(',')).join('\n');

          const blob = new Blob([csvDataString], { type: 'text/csv;charset=utf-8' });
          saveAs(blob, `${fileTitle}.csv`);
        } catch (error) {
          console.error('Failed to save chart as delimited data in childWeeklyRatingVolumeByStarRatingChart', error);
        }
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the rating volume grouped by star rating for ${entityPhrase} `}
            {taxonomyText}
            {` during the ${mainTimePeriodShortDisplayName} period.`}
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      subtitleSX={{ marginTop: '8px' }}
      includeSummaryInfo={false}
      customLegends={
        <LegendWithMultipleElement
          multipleLegendsProps={[
            { displayName: '5 Stars', barColor: 'accentPine' },
            { displayName: '4 Stars', barColor: 'success' },
            { displayName: '3 Stars', barColor: 'accentGold' },
            { displayName: '2 Stars', barColor: 'accentTangerine' },
            { displayName: '1 Star', barColor: 'accentMagenta' }
          ]}
        />
      }
      title="Weekly Rating Volume by Star Rating"
    >
      <StackedTopEntityChart
        maxNumberOfColumns={8}
        reverseAxisStartPosition
        categories={categoryData}
        chartSeries={[
          {
            type: 'column',
            data: stackedChartDataset,
            metricType: METRICTYPE.VOLUME,
            groupBy: 'weekId'
          }
        ]}
        tooltipCategoryTitle="RATING VOLUME"
        chartOptionsOverride={(option) => {
          const copiedOption = { ...option };
          copiedOption.chart.marginLeft = 40;
          copiedOption.chart.width = 1012;
          copiedOption.chart.style = {
            ...copiedOption.chart.style,
            overflow: 'hidden'
          };

          // Fix the overlapping x-axis numbers by ensuring proper label formatting
          if (copiedOption.xAxis && Array.isArray(copiedOption.xAxis)) {
            copiedOption.xAxis[0] = {
              ...copiedOption.xAxis[0],
              labels: {
                ...copiedOption.xAxis[0].labels,
                formatter: function () {
                  // Suppress numeric indices that Highcharts automatically adds
                  if (typeof this.value === 'number') {
                    return '';
                  }
                  // For weekId categories, format them properly
                  if (typeof this.value === 'string' && this.value.match(/^\d{6}$/)) {
                    const { endDate } = getStartAndEndOfWeek(this.value);
                    const formattedDate = endDate ? moment(endDate, 'YYYYMMDD').format('MMM D') : this.value;
                    return `<div style="
                      font-size: 12px;
                      text-align: center;
                      font-family: Roboto;
                      margin: 12px 0px;
                      z-index: 1000;
                      background: white;
                      padding: 2px 4px;
                      border-radius: 2px;
                      overflow: hidden;
                      white-space: nowrap;
                    ">${formattedDate}</div>`;
                  }
                  return this.value;
                }
              }
            };
          }

          // Hide y-axis labels
          if (Array.isArray(copiedOption.yAxis)) {
            copiedOption.yAxis.forEach((axis) => {
              if (axis) {
                axis.labels = { style: { color: 'transparent' } };
              }
            });
          } else if (copiedOption.yAxis) {
            copiedOption.yAxis.labels = { style: { color: 'transparent' } };
          }

          return copiedOption;
        }}
      />
    </BeaconChartWithLegend>
  );
};

export default ChildWeeklyRatingVolumeByStarRatingChart;
